import * as React from 'react'
import { Check, ChevronRight, Minus, Plus, TriangleAlert } from 'lucide-react'
import type {
  IrrigationEquipmentDto,
  IrrigationSiteDto,
  IrrigationTimerDto,
  WaterSourceDto,
} from '@lib/dto/irrigation.dto'
import {
  BACKFLOW_TYPE_LABELS,
  PIPE_SIZE_LABELS,
  SENSOR_TYPE_LABELS,
  WELL_COMPONENT_TYPE_LABELS,
  IRRIGATION_EQUIPMENT_KIND_LABELS,
  type SourceAbsentEquipment,
} from '@lib/enums/irrigation.enum'
import { MAX_ZONE_NUMBER } from '@lib/input/irrigation.input'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { ChangeAnswers } from '@/site-visits/ChangeAnswers'
import { addQuestion } from '@/site-visits/siteRecordChanges'
import { Labeled, Section } from '@/irrigation-survey/fields'
import {
  newEquipment,
  zonesToReach,
} from '@/irrigation-survey/irrigationDrafts'
import {
  lowerFirst,
  timerName,
  timerParkNote,
  waterSourceName,
  waterSourceNameInSentence,
} from '@/irrigation-survey/irrigationLabels'
import {
  equipmentWithoutParent,
  nextStepFrom,
  nextTodo,
  stepLabel,
  systemSteps,
  timersOn,
  timersWithoutSource,
  unlinkedFor,
  type StepAnchor,
  type SystemStep,
} from '@/irrigation-survey/systemSteps'
import type {
  EditorContext,
  SheetTarget,
} from '@/irrigation-survey/IrrigationEditors'

/*
 * Recording a system in the order its water runs (see systemSteps.ts). The
 * water source's sheet lists every step; each record's sheet offers the one
 * after it, so a technician can go from the meter to the zones without
 * deciding what to record next.
 */

/**
 * The equipment the city requires. Saying there is none is allowed, since it
 * is what the technician found, but it is shown as a warning, not as done.
 */
const REQUIRED: ReadonlySet<SourceAbsentEquipment> = new Set(['MASTER_VALVE'])

function isAbsentable(step: SystemStep): step is Extract<
  SystemStep,
  { kind: 'equipment' }
> & {
  equipmentKind: SourceAbsentEquipment
} {
  return step.kind === 'equipment' && step.equipmentKind !== 'WELL_COMPONENT'
}

/** Whether a step can be answered "none here". */
function offersNone(step: SystemStep): boolean {
  return isAbsentable(step) || step.kind === 'sensors'
}

function isRequiredButNone(step: SystemStep): boolean {
  return (
    isAbsentable(step) &&
    step.status === 'none' &&
    REQUIRED.has(step.equipmentKind)
  )
}

/** A record's short description on its step. */
function equipmentDetail(item: IrrigationEquipmentDto): string {
  const parts: string[] = []
  if (item.kind === 'WELL_COMPONENT' && item.wellComponentType !== null) {
    parts.push(WELL_COMPONENT_TYPE_LABELS[item.wellComponentType])
  }
  if (item.kind === 'BACKFLOW' && item.backflowType !== null) {
    parts.push(BACKFLOW_TYPE_LABELS[item.backflowType])
  }
  if (item.kind === 'SENSOR' && item.sensorType !== null) {
    parts.push(SENSOR_TYPE_LABELS[item.sensorType])
  }
  if (
    (item.kind === 'VALVE' ||
      item.kind === 'MASTER_VALVE' ||
      item.kind === 'BACKFLOW' ||
      item.kind === 'BALL_VALVE') &&
    item.size !== null
  ) {
    parts.push(PIPE_SIZE_LABELS[item.size])
  }
  if (item.part !== null) parts.push(item.part.name)
  return parts.length === 0 ? 'Details not recorded' : parts.join(' · ')
}

/**
 * A timer's row: its model, the park it stands in when its name does not say
 * so, and "Not named" once the site has another timer, because from then on
 * its zones read "Timer 2, zone 4" until it has a name.
 */
function timerDetail(
  timer: IrrigationTimerDto,
  site: IrrigationSiteDto,
): string {
  const park = timerParkNote(timer, site.timers)
  return [
    timer.label === null && site.timers.length > 1 ? 'Not named' : null,
    park === null ? null : `In ${park}`,
    timer.part?.name ?? 'Model not identified',
  ]
    .filter((part): part is string => part !== null)
    .join(' · ')
}

/** The source a step's records hang from, once the step has been resolved. */
function sourceOf(step: SystemStep, site: IrrigationSiteDto) {
  switch (step.kind) {
    case 'equipment':
    case 'timers':
      return step.source
    case 'sensors':
    case 'zones':
      return (
        site.waterSources.find(
          (source) => source.id === step.timer.waterSourceId,
        ) ?? null
      )
  }
}

/** What a step's buttons do. One place, so the list and the "Next" card agree. */
function stepActions(context: EditorContext) {
  /** The live copy, which a "none" answer is written onto. */
  function liveSource(id: string): WaterSourceDto | undefined {
    return context.site.waterSources.find((source) => source.id === id)
  }
  function liveTimer(id: string): IrrigationTimerDto | undefined {
    return context.site.timers.find((timer) => timer.id === id)
  }

  /** Say there is none, or take that back. */
  function setNone(step: SystemStep, none: boolean): string | null {
    const updatedAt = new Date().toISOString()
    if (isAbsentable(step)) {
      const source = liveSource(step.source.id)
      if (source === undefined) return null
      const others = source.absentEquipment.filter(
        (kind) => kind !== step.equipmentKind,
      )
      const result = context.commit({
        kind: 'upsert',
        entity: 'waterSource',
        record: {
          ...source,
          absentEquipment: none ? [...others, step.equipmentKind] : others,
          updatedAt,
        },
      })
      return result.ok ? null : result.message
    }
    if (step.kind === 'sensors') {
      const timer = liveTimer(step.timer.id)
      if (timer === undefined) return null
      const result = context.commit({
        kind: 'upsert',
        entity: 'timer',
        record: {
          ...timer,
          absentEquipment: none ? ['SENSOR'] : [],
          updatedAt,
        },
      })
      return result.ok ? null : result.message
    }
    return null
  }

  /**
   * Record one: placed on the map for anything with a pin of its own, the
   * sheet for a well's component (it stands at its well), the zone list for
   * zones. Adding one where "none" was said takes "none" back first.
   */
  function add(step: SystemStep): string | null {
    if (step.status === 'none') {
      const refused = setNone(step, false)
      if (refused !== null) return refused
    }
    switch (step.kind) {
      case 'equipment':
        if (step.equipmentKind === 'WELL_COMPONENT') {
          const { source } = step
          context.open({
            entity: 'equipment',
            record: newEquipment(
              context.site,
              'WELL_COMPONENT',
              {
                latitude: source.latitude,
                longitude: source.longitude,
                accuracyM: source.accuracyM,
                positionSource: source.positionSource,
              },
              { currentZoneId: null, waterSourceId: source.id },
            ),
            isNew: true,
          })
          return null
        }
        context.place({
          kind: step.equipmentKind,
          waterSourceId: step.source.id,
        })
        return null
      case 'timers':
        context.place({ kind: 'timer', waterSourceId: step.source.id })
        return null
      case 'sensors':
        context.place({ kind: 'SENSOR', timerId: step.timer.id })
        return null
      case 'zones':
        context.open({ entity: 'zones', record: step.timer, isNew: false })
        return null
    }
  }

  /** Put a record from before the steps on this step's source or timer. */
  function link(step: SystemStep, item: IrrigationEquipmentDto): string | null {
    const updatedAt = new Date().toISOString()
    let record: IrrigationEquipmentDto
    if (
      step.kind === 'equipment' &&
      (item.kind === 'MASTER_VALVE' ||
        item.kind === 'BACKFLOW' ||
        item.kind === 'BALL_VALVE')
    ) {
      record = { ...item, waterSourceId: step.source.id, updatedAt }
    } else if (step.kind === 'sensors' && item.kind === 'SENSOR') {
      record = { ...item, timerId: step.timer.id, updatedAt }
    } else {
      return null
    }
    const result = context.commit({
      kind: 'upsert',
      entity: 'equipment',
      record,
    })
    return result.ok ? null : result.message
  }

  return { add, link, setNone }
}

/** What the button that records a step says. */
function addLabel(step: SystemStep): string {
  switch (step.kind) {
    case 'equipment':
      if (step.equipmentKind === 'WELL_COMPONENT') return 'Add component'
      return step.status === 'recorded' ? 'Add another' : 'Place it'
    case 'timers':
      return step.items.length > 0 ? 'Add timer' : 'Place it'
    case 'sensors':
      return step.status === 'recorded' ? 'Add another' : 'Place it'
    case 'zones':
      return step.items.length > 0 ? 'Open zones' : 'Set up zones'
  }
}

/** The rail's marker: done, none here, the next step, or not yet. */
interface StepNodeProps {
  step: SystemStep
  index: number
  isNext: boolean
}

function StepNode({ step, index, isNext }: StepNodeProps) {
  const base =
    'flex size-6 shrink-0 items-center justify-center rounded-full border text-xs'
  if (step.status === 'recorded') {
    return (
      <span
        className={cn(base, 'border-brand bg-brand text-primary-foreground')}
      >
        <Check className="h-3.5 w-3.5" aria-label="Recorded" />
      </span>
    )
  }
  if (isRequiredButNone(step)) {
    return (
      <span
        className={cn(base, 'border-warning bg-warning-subtle text-warning')}
      >
        <TriangleAlert
          className="h-3.5 w-3.5"
          aria-label="None, but required"
        />
      </span>
    )
  }
  if (step.status === 'none') {
    return (
      <span className={cn(base, 'border-default text-muted-text')}>
        <Minus className="h-3.5 w-3.5" aria-label="None here" />
      </span>
    )
  }
  return (
    <span
      className={cn(
        base,
        isNext
          ? 'border-brand bg-brand-subtle font-medium text-brand'
          : 'border-default text-muted-text',
      )}
    >
      {index + 1}
    </span>
  )
}

/** What a step says about itself under its name. */
interface StepSummaryProps {
  step: SystemStep
}

function StepSummary({ step }: StepSummaryProps) {
  if (step.status === 'none') {
    return isRequiredButNone(step) ? (
      <p className="text-xs text-warning">None here. Required by ordinance.</p>
    ) : (
      <p className="text-xs text-muted-text">None here</p>
    )
  }
  if (step.status === 'todo') {
    return <p className="text-xs text-muted-text">Not recorded yet</p>
  }
  if (step.kind === 'zones') {
    const counted = step.items.filter((zone) =>
      zone.headCounts.some((entry) => entry.count > 0),
    ).length
    return (
      <p className="text-xs text-muted-text">
        {step.items.length} {step.items.length === 1 ? 'zone' : 'zones'},{' '}
        {counted} with heads counted
      </p>
    )
  }
  return null
}

/** The records already on a step, each opening its own sheet. */
interface StepRecordsProps {
  step: SystemStep
  context: EditorContext
}

function StepRecords({ step, context }: StepRecordsProps) {
  const { site } = context
  function row(
    key: string,
    title: string,
    detail: string,
    pendingKey: string,
    onClick: () => void,
  ) {
    return (
      <li key={key}>
        <button
          type="button"
          className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left hover:bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          onClick={onClick}
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm">{title}</span>
            <span className="block truncate text-xs text-muted-text">
              {detail}
            </span>
          </span>
          {context.pendingKeys.has(pendingKey) ? (
            <span className="text-xs text-muted-text">Not synced</span>
          ) : null}
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-text" />
        </button>
      </li>
    )
  }
  switch (step.kind) {
    case 'equipment':
    case 'sensors':
      if (step.items.length === 0) return null
      return (
        <ul className="-mx-1.5">
          {step.items.map((item) =>
            row(
              item.id,
              stepLabel(step, site),
              equipmentDetail(item),
              `equipment:${item.id}`,
              () => {
                context.open({
                  entity: 'equipment',
                  record: item,
                  isNew: false,
                })
              },
            ),
          )}
        </ul>
      )
    case 'timers':
      if (step.items.length === 0) return null
      return (
        <ul className="-mx-1.5">
          {step.items.map((timer) =>
            row(
              timer.id,
              timerName(timer, site.timers),
              timerDetail(timer, site),
              `timer:${timer.id}`,
              () => {
                context.open({ entity: 'timer', record: timer, isNew: false })
              },
            ),
          )}
        </ul>
      )
    case 'zones':
      return null
  }
}

interface UnlinkedRecordsProps {
  step: SystemStep
  items: readonly IrrigationEquipmentDto[]
  context: EditorContext
  onLink: (item: IrrigationEquipmentDto) => void
}

/**
 * Records of a step's kind from before the steps, on no source or timer.
 * Offered first, so the one already recorded is linked rather than placed a
 * second time or answered "none here".
 */
function UnlinkedRecords({
  step,
  items,
  context,
  onLink,
}: UnlinkedRecordsProps) {
  if (items.length === 0) return null
  return (
    <div className="space-y-1">
      <p className="text-xs text-secondary-text">
        Already recorded, not on{' '}
        {step.kind === 'sensors' ? 'a timer' : 'a water source'} yet. Is it this
        one?
      </p>
      <ul className="-mx-1.5">
        {items.map((item) => (
          <li key={item.id} className="flex items-center gap-2">
            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-2 rounded px-1.5 py-1 text-left hover:bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
              onClick={() => {
                context.open({
                  entity: 'equipment',
                  record: item,
                  isNew: false,
                })
              }}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm">
                  {IRRIGATION_EQUIPMENT_KIND_LABELS[item.kind]}
                </span>
                <span className="block truncate text-xs text-muted-text">
                  {equipmentDetail(item)}
                </span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-text" />
            </button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                onLink(item)
              }}
            >
              This one
            </Button>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** One step on the source's list, with what records it. */
interface StepRowProps {
  step: SystemStep
  index: number
  isNext: boolean
  isLast: boolean
  context: EditorContext
}

function StepRow({ step, index, isNext, isLast, context }: StepRowProps) {
  const actions = stepActions(context)
  const [refusal, setRefusal] = React.useState<string | null>(null)
  const unlinked =
    step.status === 'recorded' ? [] : unlinkedFor(step, context.site)
  const sourceId =
    step.kind === 'sensors' || step.kind === 'zones'
      ? step.timer.waterSourceId
      : null
  const multipleTimers =
    sourceId !== null && timersOn(context.site, sourceId).length > 1
  const label = stepLabel(step, context.site, { withTimer: multipleTimers })
  const showNone =
    offersNone(step) && step.status === 'todo' && unlinked.length === 0
  const showUndo = step.status === 'none'
  return (
    <li className="relative flex gap-3">
      {/* One hairline spine, as the process timeline draws it. */}
      {isLast ? null : (
        <span
          aria-hidden
          className="absolute bottom-0 left-3 top-7 w-px -translate-x-1/2 bg-border"
        />
      )}
      <StepNode step={step} index={index} isNext={isNext} />
      <div className="min-w-0 flex-1 space-y-1 pb-4">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <p
              className={cn(
                'text-sm font-medium',
                isNext ? 'text-brand' : 'text-primary-text',
              )}
            >
              {label}
            </p>
            <StepSummary step={step} />
          </div>
          <div className="flex shrink-0 flex-wrap justify-end gap-1">
            {showNone ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setRefusal(actions.setNone(step, true))
                }}
              >
                None here
              </Button>
            ) : null}
            {showUndo ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setRefusal(actions.setNone(step, false))
                }}
              >
                Undo
              </Button>
            ) : null}
            <Button
              type="button"
              variant={isNext ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setRefusal(actions.add(step))
              }}
            >
              {step.kind === 'zones' ? null : <Plus className="h-3.5 w-3.5" />}
              {addLabel(step)}
            </Button>
          </div>
        </div>
        {refusal === null ? null : (
          <p role="alert" className="text-sm text-danger">
            {refusal}
          </p>
        )}
        <UnlinkedRecords
          step={step}
          items={unlinked}
          context={context}
          onLink={(item) => {
            setRefusal(actions.link(step, item))
          }}
        />
        <StepRecords step={step} context={context} />
      </div>
    </li>
  )
}

/**
 * A water source's system, in the order the water runs, with the next thing
 * to record marked. The spine of the source's sheet.
 */
interface SystemChecklistProps {
  source: WaterSourceDto
  context: EditorContext
}

export function SystemChecklist({ source, context }: SystemChecklistProps) {
  const steps = systemSteps(context.site, source)
  const next = nextTodo(steps)
  return (
    <Section title="The system, in the order the water runs">
      <ol className="pt-1">
        {steps.map((step, index) => (
          <StepRow
            key={step.id}
            step={step}
            index={index}
            isNext={next?.id === step.id}
            isLast={index === steps.length - 1}
            context={context}
          />
        ))}
      </ol>
    </Section>
  )
}

/**
 * What to record after this, on a record's own sheet: finishing a backflow
 * leads on to the master valve. Not offered while the sheet has unsaved
 * edits, which going on would lose; nothing at all once the system is done.
 */
interface NextStepProps {
  /** Where the sheet stands in its source's steps; null for none. */
  anchor: StepAnchor | null
  context: EditorContext
}

export function NextStep({ anchor, context }: NextStepProps) {
  const actions = stepActions(context)
  const [refusal, setRefusal] = React.useState<string | null>(null)
  const next = anchor === null ? null : nextStepFrom(context.site, anchor)
  // Each step adds to the system, which an earlier visit does not change.
  if (next === null || context.canEditRecord === false) return null
  const unlinked = unlinkedFor(next, context.site)
  const source = sourceOf(next, context.site)
  const label = stepLabel(next, context.site, { withTimer: true })
  return (
    <div className="space-y-2 rounded-md border border-default px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs text-muted-text">
            Next
            {source === null
              ? ''
              : ` on ${waterSourceNameInSentence(source.kind, source.label)}`}
          </p>
          <p className="text-sm font-medium">{label}</p>
        </div>
        {offersNone(next) && unlinked.length === 0 ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setRefusal(actions.setNone(next, true))
            }}
          >
            None here
          </Button>
        ) : null}
        <Button
          type="button"
          size="sm"
          onClick={() => {
            setRefusal(actions.add(next))
          }}
        >
          {addLabel(next)}
        </Button>
      </div>
      <UnlinkedRecords
        step={next}
        items={unlinked}
        context={context}
        onLink={(item) => {
          setRefusal(actions.link(next, item))
        }}
      />
      {refusal === null ? null : (
        <p role="alert" className="text-sm text-danger">
          {refusal}
        </p>
      )}
    </div>
  )
}

/**
 * How many zones a timer runs. Raising it adds the missing zones, each then
 * filled in on its own sheet; lowering it is refused, because a zone is
 * removed from its own sheet, where whatever is recorded on it shows first.
 */
interface ZoneCountFieldProps {
  timer: IrrigationTimerDto
  context: EditorContext
  /** Called once zones have been added. */
  onAdded?: () => void
}

export function ZoneCountField({
  timer,
  context,
  onAdded,
}: ZoneCountFieldProps) {
  const count = context.site.zones.filter(
    (zone) => zone.timerId === timer.id,
  ).length
  // Shows the timer's live count until somebody types, so a zone added or
  // removed elsewhere while the sheet is open never leaves a stale number
  // one tap from being applied.
  const [typed, setTyped] = React.useState<string | null>(null)
  const [message, setMessage] = React.useState<string | null>(null)
  const value = typed ?? (count === 0 ? '' : String(count))
  const wanted = Number(value)
  const isNumber = value.trim() !== '' && Number.isInteger(wanted) && wanted > 0
  const id = `zone-count-${timer.id}`

  /*
   * A timer's first zones are its record being filled in, whatever visit
   * they are counted on, as a zone's first outline is. More zones on a timer
   * that has some could be either, so a revisit asks which, on the button
   * (docs/site-visits.md).
   */
  const question =
    count > 0 && context.isRevisit === true ? addQuestion('ZONE') : null

  function addZones(isCorrection?: boolean, changedOn?: string): void {
    if (!isNumber) return
    if (wanted > MAX_ZONE_NUMBER) {
      setMessage(`A timer runs at most ${String(MAX_ZONE_NUMBER)} zones.`)
      return
    }
    if (wanted < count) {
      setMessage(
        `This timer has ${String(count)} zones. To have fewer, remove a zone from its own sheet.`,
      )
      return
    }
    for (const zone of zonesToReach(context.site, timer.id, wanted)) {
      const result = context.commit(
        { kind: 'upsert', entity: 'zone', record: zone },
        count === 0 ? true : isCorrection,
        changedOn,
      )
      if (!result.ok) {
        setMessage(result.message)
        return
      }
    }
    setMessage(null)
    setTyped(null)
    onAdded?.()
  }

  return (
    <Labeled
      label="Zones on this timer"
      id={id}
      hint={
        count === 0
          ? 'How many zones the timer runs. Each gets its own sheet to fill in.'
          : undefined
      }
    >
      <div className="flex gap-2">
        <input
          id={id}
          inputMode="numeric"
          className="h-11 w-24 rounded-md border border-default bg-surface px-2.5 text-sm text-primary-text transition-colors hover:border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 lg:h-9"
          value={value}
          placeholder="e.g. 12"
          onChange={(event) => {
            setTyped(event.target.value)
            setMessage(null)
          }}
          onKeyDown={(event) => {
            // Enter cannot say which a revisit's zones are; the buttons do.
            if (event.key === 'Enter' && question === null) addZones()
          }}
        />
        {question === null ? (
          <Button
            type="button"
            variant="outline"
            className="h-11 lg:h-9"
            disabled={!isNumber || wanted === count}
            onClick={() => {
              addZones()
            }}
          >
            {count === 0 ? 'Create zones' : 'Add zones'}
          </Button>
        ) : (
          <ChangeAnswers
            question={question}
            size="sm"
            buttonClassName="h-11 lg:h-9"
            disabled={!isNumber || wanted === count}
            // Counted in no visit: say when the zones were added too.
            askWhen={context.asksWhen === true}
            onAnswer={addZones}
          />
        )}
      </div>
      {message === null ? null : (
        <p role="alert" className="text-sm text-danger">
          {message}
        </p>
      )}
    </Labeled>
  )
}

interface SystemBarProps {
  site: IrrigationSiteDto
  onOpen: (target: SheetTarget) => void
}

/**
 * Under the map: each water source and what it still needs, so the next
 * step is one tap from anywhere on the site. Timers and equipment recorded
 * before the steps existed, on no source or timer, are listed until somebody
 * says which.
 */
export function SystemBar({ site, onOpen }: SystemBarProps) {
  const rowClass =
    'flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40'
  return (
    <ul className="divide-y divide-default rounded-md border border-default">
      {site.waterSources.map((source) => {
        const steps = systemSteps(site, source)
        const next = nextTodo(steps)
        const warn = steps.some(isRequiredButNone)
        return (
          <li key={source.id}>
            <button
              type="button"
              className={rowClass}
              onClick={() => {
                onOpen({ entity: 'waterSource', record: source, isNew: false })
              }}
            >
              <span className="min-w-0 flex-1 truncate text-sm font-medium">
                {waterSourceName(source.kind, source.label)}
              </span>
              {warn ? (
                <TriangleAlert
                  className="h-3.5 w-3.5 shrink-0 text-warning"
                  aria-label="Required equipment missing"
                />
              ) : null}
              <span className="shrink-0 text-xs text-muted-text">
                {next === null
                  ? 'System recorded'
                  : `Next: ${lowerFirst(stepLabel(next, site, { withTimer: true }))}`}
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-text" />
            </button>
          </li>
        )
      })}
      {timersWithoutSource(site).map((timer) => (
        <li key={timer.id}>
          <button
            type="button"
            className={rowClass}
            onClick={() => {
              onOpen({ entity: 'timer', record: timer, isNew: false })
            }}
          >
            <span className="min-w-0 flex-1 truncate text-sm font-medium">
              {timerName(timer, site.timers)}
            </span>
            <span className="shrink-0 text-xs text-warning">
              Which water source?
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-text" />
          </button>
        </li>
      ))}
      {equipmentWithoutParent(site).map((item) => (
        <li key={item.id}>
          <button
            type="button"
            className={rowClass}
            onClick={() => {
              onOpen({ entity: 'equipment', record: item, isNew: false })
            }}
          >
            <span className="min-w-0 flex-1 truncate text-sm font-medium">
              {IRRIGATION_EQUIPMENT_KIND_LABELS[item.kind]}
            </span>
            <span className="shrink-0 text-xs text-warning">
              {item.kind === 'SENSOR' ? 'Which timer?' : 'Which water source?'}
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-text" />
          </button>
        </li>
      ))}
    </ul>
  )
}
